const { Mixin } = Shopware;
const DAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

export default {
    template: `
<sw-page class="riccardo-store-detail">
    <template #smart-bar-header><h2>{{ store?.name || 'Neue Filiale' }}</h2></template>
    <template #smart-bar-actions>
        <sw-button :router-link="{ name: 'riccardo.store.list' }">Abbrechen</sw-button>
        <sw-button-process variant="primary" :is-loading="isLoading" :process-success="isSaveSuccessful" @click="onSave">Speichern</sw-button-process>
    </template>
    <template #content>
        <sw-card-view v-if="store">
            <sw-card title="Filiale" :is-loading="isLoading">
                <sw-switch-field v-model:value="store.active" label="Aktiv (im Filialfinder sichtbar)" />
                <sw-container columns="2fr 1fr" gap="0 24px">
                    <sw-text-field v-model:value="store.name" label="Name" required @update:value="onNameChange" />
                    <sw-text-field v-model:value="store.slug" label="URL-Kürzel" help-text="Adresse der Detailseite: /filialen/<Kürzel>" required />
                </sw-container>
                <sw-container columns="2fr 1fr 1fr" gap="0 24px">
                    <sw-text-field v-model:value="store.street" label="Straße und Hausnummer" />
                    <sw-text-field v-model:value="store.zip" label="PLZ" />
                    <sw-text-field v-model:value="store.city" label="Ort" />
                </sw-container>
                <sw-container columns="1fr 1fr 1fr 1fr" gap="0 24px">
                    <sw-text-field v-model:value="store.phone" label="Telefon" />
                    <sw-text-field v-model:value="store.email" label="E-Mail" />
                    <sw-number-field v-model:value="store.latitude" number-type="float" :digits="6" label="Breitengrad" help-text="z. B. 50.157649 – für Entfernung und Karte" />
                    <sw-number-field v-model:value="store.longitude" number-type="float" :digits="6" label="Längengrad" help-text="z. B. 8.634447" />
                </sw-container>
                <sw-number-field v-model:value="store.position" number-type="int" label="Sortierung" />
            </sw-card>

            <sw-card title="Öffnungszeiten" :is-loading="isLoading">
                <div v-for="row in hours" :key="row.day" style="display: grid; grid-template-columns: 140px 120px 1fr 1fr; gap: 16px; align-items: center;">
                    <strong>{{ dayName(row.day) }}</strong>
                    <sw-checkbox-field v-model:value="row.closed" label="geschlossen" />
                    <sw-text-field v-model:value="row.open" :disabled="row.closed" placeholder="09:00" label="von" />
                    <sw-text-field v-model:value="row.close" :disabled="row.closed" placeholder="20:00" label="bis" />
                </div>
            </sw-card>

            <sw-card title="Darstellung" :is-loading="isLoading">
                <sw-multi-tag-select v-model:value="store.services" label="Services" placeholder="z. B. Liquids probieren, Enter drücken" />
                <sw-upload-listener :upload-tag="uploadTag" auto-upload @media-upload-finish="onMediaUpload" />
                <sw-media-upload-v2 :upload-tag="uploadTag" :source="store.mediaId" label="Filialfoto" default-folder="cms_page" @media-drop="onMediaDrop" @media-upload-remove-image="store.mediaId = null" />
                <sw-textarea-field v-model:value="store.description" label="Beschreibung (HTML erlaubt)" />
            </sw-card>
        </sw-card-view>
    </template>
</sw-page>`,

    inject: ['repositoryFactory'],

    mixins: [Mixin.getByName('notification')],

    props: {
        storeId: { type: String, required: false, default: null },
    },

    data() {
        return { store: null, hours: [], isLoading: false, isSaveSuccessful: false, uploadTag: 'riccardo-store-media' };
    },

    computed: {
        storeRepository() {
            return this.repositoryFactory.create('riccardo_store');
        },
    },

    created() {
        this.load();
    },

    methods: {
        dayName(day) {
            return DAYS[day - 1];
        },

        buildHours(existing) {
            const byDay = {};
            (existing || []).forEach((row) => { byDay[row.day] = row; });
            return [1, 2, 3, 4, 5, 6, 7].map((day) => ({
                day,
                open: byDay[day]?.open || '',
                close: byDay[day]?.close || '',
                closed: byDay[day] ? !!byDay[day].closed : day === 7,
            }));
        },

        load() {
            if (!this.storeId) {
                this.store = this.storeRepository.create();
                this.store.active = true;
                this.store.country = 'Deutschland';
                this.store.position = 0;
                this.store.services = [];
                this.hours = this.buildHours([]);
                return Promise.resolve();
            }
            this.isLoading = true;
            return this.storeRepository.get(this.storeId).then((store) => {
                this.store = store;
                this.store.services = store.services || [];
                this.hours = this.buildHours(store.openingHours);
            }).finally(() => { this.isLoading = false; });
        },

        onNameChange(value) {
            if (this.store.slug || !value) {
                return;
            }
            this.store.slug = value.toLowerCase()
                .replace(/^ric+ar?do\s+store\s+/, '')
                .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
                .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        },

        onMediaUpload({ targetId }) {
            this.store.mediaId = targetId;
        },

        onMediaDrop(media) {
            this.store.mediaId = media.id;
        },

        onSave() {
            this.store.openingHours = this.hours.map((row) => (row.closed || !row.open
                ? { day: row.day, closed: true }
                : { day: row.day, open: row.open, close: row.close, closed: false }));
            this.isLoading = true;
            this.isSaveSuccessful = false;
            return this.storeRepository.save(this.store).then(() => {
                this.isSaveSuccessful = true;
                this.createNotificationSuccess({ message: 'Filiale gespeichert' });
                if (!this.storeId) {
                    this.$router.push({ name: 'riccardo.store.detail', params: { id: this.store.id } });
                    return Promise.resolve();
                }
                return this.load();
            }).catch((error) => {
                this.createNotificationError({ message: error?.response?.data?.errors?.[0]?.detail || 'Filiale konnte nicht gespeichert werden' });
            }).finally(() => { this.isLoading = false; });
        },
    },
};
